import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

import { ResponseModel } from '../domain/authentication.model'
import { AppType } from '../types/app.type'
import { ThemeTitleType } from '../types/color-settings.type'
import { EnvironmentService } from './environment.service'
import { I18nService, LgType } from './prod/i18n.service'
import { WidgetsApiService } from './widget/widgets-api.service'
import { Widget } from './widget/widgets-api.type'

export interface IColors {
  color: string
  bgColor: string
  borderWidth: string
  borderStyle: string
  borderColor: string
}

export interface IStyleItems {
  borderAndBgConnected: boolean
  colors: IColors
  id: number
  isChecked: boolean
  selectors: Array<string>
  // В title указано app: AppType. Это значит, что эти блоки не нужно показывать в указанном app
  // Первоначальная задумка возможно была не для этого, т.е. это свойство уже было, но нигде не использовалось
  title: string
}

export interface IColorGroup {
  title: string
  styleItems: {
    [key: string]: IStyleItems
  }
  app: AppType[]
}

@Injectable({
  providedIn: 'root'
})
export class SetColorsService {
  colorSettingsSubject = new BehaviorSubject<boolean>(false)

  lg: LgType
  groups: Array<IColorGroup> = []
  currentThemeTitle: ThemeTitleType

  private styleElement = null
  private styleElementId = 'set_all_color'

  private widget: Widget

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService,
    private i18nService: I18nService,
    private readonly widgetsApiService: WidgetsApiService
  ) {
    this.lg = this.i18nService.getLanguageForPage()
    this.setGroupsTheme()
  }

  setGroupsTheme(themeTitle?: ThemeTitleType) {
    let mainBg
    let addBg
    let mainText
    let addText
    let mainBorder
    let highlight

    // header & footer
    let hfBg

    // beat
    let hoverBeat
    let activeBeat

    // Buttons
    let mainBtnColor
    let mainBtnBg
    let mainBtnBorder
    let addBtnColor
    let addBtnBg
    let disabledBtnBg

    // Inputs
    let mainInputBg
    let mainInputBorder
    let mainInputPlaceholder

    // Player
    let progressBar
    let loadingBar

    switch (themeTitle) {
      case 'light':
        hfBg = '#101010'

        highlight = '#FC6A6A'
        mainBg = '#EBEBEB'
        addBg = '#E3E3E3'
        mainText = '#2A2A2A'
        addText = '#8D8D8D'
        mainBorder = '#DDDDDD'

        hoverBeat = '#F6E0E2'
        activeBeat = '#ECE4E4'

        mainBtnColor = '#FFFFFF'
        mainBtnBg = '#FC6A6A'
        mainBtnBorder = '#FC6A6A'
        addBtnColor = '#FC6A6A'
        addBtnBg = '#F6E0E2'
        disabledBtnBg = '#F4ABAB'

        mainInputBg = '#D5D5D5'
        mainInputBorder = '#C9C9C9'
        mainInputPlaceholder = '#8D8D8D'

        progressBar = '#D5D5D5'
        loadingBar = '#DCB8B8'
        break

      case 'dark-pink':
        hfBg = '#190225'

        highlight = '#C10A4C'
        mainBg = '#190225'
        addBg = '#1E042C'
        mainText = '#FFFFFF'
        addText = '#8C8092'
        mainBorder = '#291434'

        hoverBeat = '#2A0329'
        activeBeat = '#210227'

        mainBtnColor = '#FFFFFF'
        mainBtnBg = '#C10A4C'
        mainBtnBorder = '#C10A4C'
        addBtnColor = '#FFFFFF'
        addBtnBg = '#31032B'
        disabledBtnBg = '#6B0635'

        mainInputBg = '#260538'
        mainInputBorder = '#351746'
        mainInputPlaceholder = '#92829B'

        progressBar = '#6F6375'
        loadingBar = '#98657D'
        break

      case 'dark-orange':
        hfBg = '#101010'

        highlight = '#FFA317'
        mainBg = '#101010'
        addBg = '#171717'
        mainText = '#FFFFFF'
        addText = '#878787'
        mainBorder = '#212121'

        hoverBeat = '#281F11'
        activeBeat = '#1C1710'

        mainBtnColor = '#FFFFFF'
        mainBtnBg = '#FFA317'
        mainBtnBorder = '#FFA317'
        addBtnColor = '#FFFFFF'
        addBtnBg = '#281F11'
        disabledBtnBg = '#875914'

        mainInputBg = '#171717'
        mainInputBorder = '#272727'
        mainInputPlaceholder = '#8B8B8B'

        progressBar = '#6A6A6A'
        loadingBar = '#A68F6C'
        break

      case 'dark-neon':
        hfBg = '#0E1226'

        highlight = '#029EA1'
        mainBg = '#0E1226'
        addBg = '#101630'
        mainText = '#FFFFFF'
        addText = '#868892'
        mainBorder = '#1F2335'

        hoverBeat = '#0D2032'
        activeBeat = '#0D192C'

        mainBtnColor = '#fff'
        mainBtnBg = '#029EA1'
        mainBtnBorder = '#029EA1'
        addBtnColor = '#FFFFFF'
        addBtnBg = '#0C2D3D'
        disabledBtnBg = '#085863'

        mainInputBg = '#0D0F1D'
        mainInputBorder = '#1E202D'
        mainInputPlaceholder = '#86878E'

        progressBar = '#696B75'
        loadingBar = '#668E94'
        break

      case 'custom':
        return this.groups

      case 'dark-red':
      default:
        hfBg = '#101010'

        highlight = '#E6242F'
        mainBg = '#101010'
        addBg = '#171717'
        mainText = '#FFFFFF'
        addText = '#878787'
        mainBorder = '#272727'

        hoverBeat = '#251213'
        activeBeat = '#1B1112'

        mainBtnColor = '#FFFFFF'
        mainBtnBg = '#E6242F'
        mainBtnBorder = '#E6242F'
        addBtnColor = '#FFFFFF'
        addBtnBg = '#2B1012'
        disabledBtnBg = '#7B1A1F'

        mainInputBg = '#171717'
        mainInputBorder = '#272727'
        mainInputPlaceholder = '#8B8B8B'

        progressBar = '#6A6A6A'
        loadingBar = '#A06F72'
        break
    }

    const co = this.createObj

    this.groups = [
      // Background for app (cart and catalog pages )
      {
        title: this.lg.c.app.heading,
        styleItems: {
          [this.lg.c.app.bg]: co(5, ['.theme-app__bg'], '', mainBg, '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // HEADER
      {
        title: this.lg.c.headerBlock.heading,
        styleItems: {
          [this.lg.c.headerBlock.bg]: co(112, ['.theme-header'], '', hfBg, ''),
          [this.lg.c.headerBlock.logoBeatmakerName]: co(118, ['.theme-header__beatmaker-name'], '#FFFFFF', '', ''),
          [this.lg.c.headerBlock.cartIcon]: co(117, ['.theme-header__cart-icon'], '#FFFFFF', '', ''),
          [this.lg.c.headerBlock.cartCounter]: co(133, ['.theme-header__cart-icon-count'], '#FFFFFF', highlight, ''),
          [this.lg.c.headerBlock.links]: co(113, ['.theme-header__link'], '#FFFFFF', '', '', 'WIDGET'),
          [this.lg.c.headerBlock.activeLink]: co(115, ['.theme-header__link--active'], highlight, '', '', 'WIDGET'),
          [this.lg.c.headerBlock.activeLinkLine]: co(
            207,
            ['.theme-header__activeLinkLine'],
            '',
            highlight,
            '',
            'WIDGET'
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Beats block on HOME page
      {
        title: this.lg.c.beatsBlock.heading,
        styleItems: {
          [this.lg.c.beatsBlock.bg]: co(1, ['.theme-beats__bg'], '', mainBg, ''),
          [this.lg.c.beatsBlock.title]: co(46, ['.theme-beats__title'], mainText, '', ''),
          [this.lg.c.beatsBlock.btn]: co(47, ['.theme-btn__beatsBlock'], mainBtnColor, mainBtnBg, mainBtnBorder)
        },
        app: ['PROD-PAGE']
      },
      // Beat (Used in beats blocks on HOME / BEAT pages)
      {
        title: this.lg.c.beat.heading,
        styleItems: {
          [this.lg.c.beat.bg]: co(2, ['.theme-beats__beat-bg'], '', mainBg, ''),
          [this.lg.c.beat.activeBg]: co(48, ['.theme-beats__beat-bg-active'], '', activeBeat, ''),
          [this.lg.c.beat.hoverBg]: co(49, ['.theme-beats__beat-bg-hover'], '', hoverBeat, ''),
          [this.lg.c.beat.dividerLine]: co(50, ['.theme-beats__beatDividerLine'], '', mainBorder, ''),
          [this.lg.c.beat.name]: co(3, ['.theme-beats__beat-name'], mainText, '', ''),
          [this.lg.c.beat.descr]: co(4, ['.theme-beats__beat-descr'], addText, '', ''),
          [this.lg.c.beat.downloadBeatIcon]: co(8, ['.theme-beats__downloadBeatIcon'], mainText, '', ''),
          [this.lg.c.beat.shareBeatIcon]: co(203, ['.theme-beats__shareBeatIcon'], mainText, '', ''),
          [this.lg.c.beat.btnBuy]: co(39, ['.theme-btn__beatsBlock-buy'], addBtnColor, addBtnBg, mainBtnBorder),
          [this.lg.c.beat.btnIconBuy]: co(40, ['.theme-icon__beatsBlock-buy'], addBtnColor, '', ''),
          [this.lg.c.beat.btnInCart]: co(41, ['.theme-btn__beatsBlock-inCart'], mainBtnColor, mainBtnBg, mainBtnBorder),
          [this.lg.c.beat.btnIconInCart]: co(42, ['.theme-icon__beatsBlock-inCart'], mainBtnColor, '', ''),
          [this.lg.c.beat.dotsIcon]: co(85, ['.theme-beats__dotsIcon'], mainText, '', ''),
          [this.lg.c.beat.dropdownControlsBg]: co(94, ['.theme-beats__dropdownControlsBg'], '', mainBg, ''),
          [this.lg.c.beat.dropdownControlsIcons]: co(206, ['.theme-beats__dropdownControlsIcons'], mainText, '', '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // License block on Home page
      {
        title: this.lg.c.licenseBlock.heading,
        styleItems: {
          [this.lg.c.licenseBlock.bg]: co(17, ['.theme-licenses__bg'], '', mainBg, ''),
          [this.lg.c.licenseBlock.title]: co(10, ['.theme-licenses__title'], mainText, '', '')
        },
        app: ['PROD-PAGE']
      },
      // License (Used in license block on HOME / BEAT pages)
      {
        title: this.lg.c.license.heading,
        styleItems: {
          [this.lg.c.license.topBlock]: co(11, ['.theme-licenses__itemTopBlock'], mainText, addBg, ''),
          [this.lg.c.license.arrows]: co(218, ['.theme-licenses__arrows'], mainText, '', ''),
          [this.lg.c.license.dots]: co(219, ['.theme-licenses__dots'], '', mainText, mainText),
          [this.lg.c.license.mainBlock]: co(12, ['.theme-licenses__itemMainBlock'], mainText, mainBg, mainBorder),
          [this.lg.c.license.listIcon]: co(14, ['.theme-licenses__itemListIcon-after'], '', highlight, ''),
          [this.lg.c.license.bottomBlock]: co(13, ['.theme-licenses__itemBottomBlock'], mainText, addBg, ''),
          [this.lg.c.license.btnShowAllBeats]: co(
            16,
            ['.theme-btn__licenseBlock-showAllBeats'],
            addBtnColor,
            addBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.license.btnShowAllBeatsIcon]: co(
            18,
            ['.theme-icon__licenseBlock-showAllBeats'],
            addBtnColor,
            '',
            ''
          ),
          [this.lg.c.license.btnAddToCart]: co(
            30,
            ['.theme-btn__licenseBlock-addToCart'],
            addBtnColor,
            addBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.license.btnIconAddToCart]: co(31, ['.theme-icon__licenseBlock-addToCart'], addBtnColor, '', ''),
          [this.lg.c.license.btnGoToCart]: co(
            51,
            ['.theme-btn__licenseBlock-goToCart'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.license.btnIconGoToCart]: co(52, ['.theme-icon__licenseBlock-goToCart'], mainBtnColor, '', ''),
          [this.lg.c.license.discountIcon]: co(15, ['.theme-licenses__itemDiscountIcon'], highlight, '', ''),
          [this.lg.c.license.recommended]: co(222, ['.theme-licenses__recommended'], '', highlight, '')
        },
        app: ['PROD-PAGE']
      },
      // Contacts
      {
        title: this.lg.c.contactsBlock.heading,
        styleItems: {
          [this.lg.c.contactsBlock.bg]: co(26, ['.theme-contacts__bg'], '', mainBg, ''),
          [this.lg.c.contactsBlock.title]: co(20, ['.theme-contacts__title'], mainText, '', ''),
          [this.lg.c.contactsBlock.descr]: co(21, ['.theme-contacts__descr'], mainText, '', ''),
          [this.lg.c.contactsBlock.requiredField]: co(22, ['.theme-contacts__requiredField'], addText, '', ''),
          [this.lg.c.contactsBlock.input]: co(
            23,
            ['.theme-input__contactsBlock'],
            mainText,
            mainInputBg,
            mainInputBorder
          ),
          [this.lg.c.contactsBlock.inputPlaceholder]: co(
            24,
            ['.theme-input__contactsBlock-placeholder'],
            mainInputPlaceholder,
            '',
            ''
          ),
          [this.lg.c.contactsBlock.btn]: co(25, ['.theme-btn__contactsBlock'], mainBtnColor, mainBtnBg, mainBtnBorder),
          [this.lg.c.contactsBlock.btnIcon]: co(27, ['.theme-icon__contactsBlock'], mainBtnColor, '', ''),
          [this.lg.c.contactsBlock.socIcons]: co(29, ['.theme-contacts__socIcons'], mainBtnColor, mainBtnBg, '')
        },
        app: ['PROD-PAGE']
      },
      // Newsletter
      {
        title: this.lg.c.newsLetterBlock.heading,
        styleItems: {
          [this.lg.c.newsLetterBlock.bg]: co(32, ['.theme-newsletter'], '', addBg, ''),
          [this.lg.c.newsLetterBlock.title]: co(33, ['.theme-newsletter__title'], mainText, '', ''),
          [this.lg.c.newsLetterBlock.text]: co(35, ['.theme-newsletter__text'], mainText, '', ''),
          [this.lg.c.newsLetterBlock.input]: co(
            44,
            ['.theme-input__newsLetterBlock'],
            mainText,
            mainInputBg,
            mainInputBorder
          ),
          [this.lg.c.newsLetterBlock.inputPlaceholder]: co(
            45,
            ['.theme-input__newsLetterBlock-placeholder'],
            mainInputPlaceholder,
            '',
            ''
          ),
          [this.lg.c.newsLetterBlock.btn]: co(
            37,
            ['.theme-btn__newsLetterBlock'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.newsLetterBlock.btnIcon]: co(38, ['.theme-icon__newsLetterBlock'], mainBtnColor, '', '')
        },
        app: ['PROD-PAGE']
      },
      // Footer
      {
        title: this.lg.c.footer.heading,
        styleItems: {
          [this.lg.c.footer.bg]: co(62, ['.theme-footer'], '', hfBg, ''),
          [this.lg.c.footer.logo]: co(63, ['.theme-footer__logo'], '#FFFFFF', '', ''),
          [this.lg.c.footer.info]: co(64, ['.theme-footer__info'], '#FFFFFF', '', ''),
          [this.lg.c.footer.linkGroupTitle]: co(65, ['.theme-footer__linkGroupTitle'], '#FFFFFF', '', ''),
          [this.lg.c.footer.links]: co(66, ['.theme-footer__links'], '#FFFFFF', '', '')
        },
        app: ['PROD-PAGE']
      },
      // MU Select in footer (language)
      {
        title: this.lg.c.selectMU.heading.lang,
        styleItems: {
          [this.lg.c.selectMU.select.border]: co(210, ['.theme-select__selectBorder--lang'], addText, '', ''),
          [this.lg.c.selectMU.select.borderHover]: co(
            214,
            ['.theme-select__selectBorderHover--lang'],
            '#4F4D4D',
            '',
            ''
          ),
          [this.lg.c.selectMU.select.groupName]: co(211, ['.theme-select__selectGroupName--lang'], addText, '', ''),
          [this.lg.c.selectMU.select.currentValue]: co(
            212,
            ['.theme-select__selectCurrentValue--lang'],
            '#FFFFFF',
            '',
            ''
          ),
          [this.lg.c.selectMU.select.arrow]: co(213, ['.theme-select__selectArrow--lang'], '#FFFFFF', '', ''),
          [this.lg.c.selectMU.options.item]: co(216, ['.theme-select__option--lang'], '#FFFFFF', hfBg, ''),
          [this.lg.c.selectMU.options.selectedItem]: co(
            215,
            ['.theme-select__selectedOption--lang'],
            highlight,
            hoverBeat,
            ''
          ),
          [this.lg.c.selectMU.options.hoverItem]: co(
            217,
            ['.theme-select__hoveredOption-hover--lang'],
            highlight,
            hoverBeat,
            ''
          )
        },
        app: ['PROD-PAGE']
      },
      // Catalog page (common block)
      {
        title: this.lg.c.catalogPage.heading,
        styleItems: {
          [this.lg.c.catalogPage.title]: co(6, ['.theme-catalog-page__title'], mainText, '', '')
        },
        app: ['PROD-PAGE']
      },
      // MU Select (find tracks)
      {
        title: this.lg.c.selectMU.heading.findTracks,
        styleItems: {
          [this.lg.c.selectMU.select.border]: co(158, ['.theme-select__selectBorder--findTracks'], addText, '', ''),
          [this.lg.c.selectMU.select.borderHover]: co(
            159,
            ['.theme-select__selectBorderHover--findTracks'],
            '#4F4D4D',
            '',
            ''
          ),
          [this.lg.c.selectMU.select.groupName]: co(
            169,
            ['.theme-select__selectGroupName--findTracks'],
            addText,
            '',
            ''
          ),
          [this.lg.c.selectMU.select.currentValue]: co(
            177,
            ['.theme-select__selectCurrentValue--findTracks'],
            mainText,
            '',
            ''
          ),
          [this.lg.c.selectMU.select.arrow]: co(178, ['.theme-select__selectArrow--findTracks'], mainText, '', ''),
          [this.lg.c.selectMU.options.item]: co(179, ['.theme-select__option--findTracks'], mainText, mainBg, ''),
          [this.lg.c.selectMU.options.selectedItem]: co(
            180,
            ['.theme-select__selectedOption--findTracks'],
            highlight,
            hoverBeat,
            ''
          ),
          [this.lg.c.selectMU.options.hoverItem]: co(
            181,
            ['.theme-select__hoveredOption-hover--findTracks'],
            highlight,
            hoverBeat,
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // MU Input (find tracks)
      {
        title: this.lg.c.inputMU.heading,
        styleItems: {
          [this.lg.c.inputMU.border]: co(192, ['.theme-inputMU__border'], addText, '', ''),
          [this.lg.c.inputMU.borderHover]: co(193, ['.theme-inputMU__borderHover'], '#4F4D4D', '', ''),
          [this.lg.c.inputMU.groupName]: co(194, ['.theme-inputMU__groupName'], addText, '', ''),
          [this.lg.c.inputMU.searchText]: co(198, ['.theme-inputMU__searchText'], mainText, '', ''),
          [this.lg.c.inputMU.closeIcon]: co(199, ['.theme-inputMU__closeIcon'], mainText, '', ''),
          [this.lg.c.inputMU.tags.list]: co(195, ['.theme-inputMU__resultsList'], '', mainBg, '#4F4D4D'),
          [this.lg.c.inputMU.tags.item]: co(196, ['.theme-inputMU__resultsTag'], mainText, mainBg, mainBorder),
          [this.lg.c.inputMU.tags.notFoundText]: co(197, ['.theme-inputMU__resultsNotFoundText'], mainText, '', '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Catalog page (Beats not found)
      {
        title: this.lg.c.notFoundMessage.heading,
        styleItems: {
          [this.lg.c.notFoundMessage.mainImg]: co(189, ['.theme-notFoundMessage__mainImg'], mainText, '', ''),
          [this.lg.c.notFoundMessage.mainTitle]: co(190, ['.theme-notFoundMessage__mainTitle'], mainText, '', ''),
          [this.lg.c.notFoundMessage.descr]: co(191, ['.theme-notFoundMessage__descr'], mainText, '', '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Checkout - 1
      {
        title: this.lg.c.cartPage.heading,
        styleItems: {
          [this.lg.c.cartPage.title]: co(91, ['.theme-cartPage__title'], mainText, '', ''),
          [this.lg.c.cartPage.input]: co(92, ['.theme-cartPage__input'], mainText, mainInputBg, mainInputBorder),
          [this.lg.c.cartPage.inputPlaceholder]: co(
            96,
            ['.theme-cartPage__input-placeholder'],
            mainInputPlaceholder,
            '',
            ''
          ),
          [this.lg.c.cartPage.btnCoupon]: co(
            97,
            ['.theme-btn__cartPage-btnCoupon'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.cartPage.btnCouponIcon]: co(98, ['.theme-icon__cartPage-btnCouponIcon'], mainBtnColor, '', ''),
          [this.lg.c.cartPage.disableBtnCoupon]: co(
            99,
            ['.theme-btn__cartPage-disabledBtnCoupon'],
            '#FFFFFF',
            disabledBtnBg,
            disabledBtnBg
          ),
          [this.lg.c.cartPage.disableBtnCouponIcon]: co(
            100,
            ['.theme-icon__cartPage-disabledBtnCouponIcon'],
            '#FFFFFF',
            '',
            ''
          ),
          [this.lg.c.cartPage.beatPrice]: co(128, ['.theme-cartPage__beatPrice'], mainText, '', ''),
          [this.lg.c.cartPage.discountPrice]: co(129, ['.theme-cartPage__discountPrice'], addText, '', ''),
          [this.lg.c.cartPage.editLicenseIcon]: co(201, ['.theme-cartPage__editLicenseIcon'], mainText, '', ''),
          [this.lg.c.cartPage.deleteBeatIcon]: co(202, ['.theme-cartPage__deleteBeatIcon'], mainText, '', ''),
          [this.lg.c.cartPage.btnBackToTracksPage]: co(
            223,
            ['.theme-btn__cartPage-btnBackToTracksPage'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder,
            'PROD-PAGE'
          ),
          [this.lg.c.cartPage.btnBackToTracksPageIcon]: co(
            224,
            ['.theme-icon__cartPage-btnBackToTracksPageIcon'],
            mainBtnColor,
            '',
            '',
            'PROD-PAGE'
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Checkout - 2 (cartPagePaymentBlock)
      {
        title: this.lg.c.cartPagePaymentBlock.heading,
        styleItems: {
          [this.lg.c.cartPagePaymentBlock.bg]: co(101, ['.theme-cartPagePaymentBlock__bg'], '', mainBg, mainBorder),
          [this.lg.c.cartPagePaymentBlock.infoKey]: co(102, ['.theme-cartPagePaymentBlock__infoKey'], addText, '', ''),
          [this.lg.c.cartPagePaymentBlock.infoValue]: co(
            103,
            ['.theme-cartPagePaymentBlock__infoValue'],
            mainText,
            '',
            ''
          ),
          [this.lg.c.cartPagePaymentBlock.infoDividerLine]: co(
            104,
            ['.theme-cartPagePaymentBlock__infoDividerLine'],
            '',
            mainBorder,
            ''
          ),
          [this.lg.c.cartPagePaymentBlock.paymentTitle]: co(
            107,
            ['.theme-cartPagePaymentBlock__paymentTitle'],
            mainText,
            '',
            ''
          ),
          [this.lg.c.cartPagePaymentBlock.discountTitle]: co(
            108,
            ['.theme-cartPagePaymentBlock__discountTitle'],
            addText,
            '',
            ''
          ),
          [this.lg.c.cartPagePaymentBlock.finalCost]: co(109, ['.theme-cartPagePaymentBlock__price'], mainText, '', ''),
          [this.lg.c.cartPagePaymentBlock.btnConfirm]: co(
            105,
            ['.theme-btn__cartPagePaymentBlock-btnConfirm'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.cartPagePaymentBlock.btnConfirmIcon]: co(
            106,
            ['.theme-icon__cartPagePaymentBlock-btnConfirmIcon'],
            mainBtnColor,
            '',
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Beat page (header block)
      {
        title: this.lg.c.headerBlockBeatPage.heading,
        styleItems: {
          [this.lg.c.headerBlockBeatPage.beatName]: co(53, ['.theme-headerBlockBeatPage__beatName'], '#FFFFFF', '', ''),
          [this.lg.c.headerBlockBeatPage.playBtn]: co(
            57,
            ['.theme-headerBlockBeatPage__playBtn'],
            '#FFFFFF',
            highlight,
            ''
          ),
          [this.lg.c.headerBlockBeatPage.beatInfo]: co(55, ['.theme-headerBlockBeatPage__beatInfo'], '#FFFFFF', '', ''),
          [this.lg.c.headerBlockBeatPage.beatInfoIcon]: co(
            56,
            ['.theme-headerBlockBeatPage__beatInfoIcon'],
            '#FFFFFF',
            '',
            ''
          ),
          [this.lg.c.headerBlockBeatPage.downloadBeatBtn]: co(
            81,
            ['.theme-headerBlockBeatPage__downloadBeatBtn'],
            '#FFFFFF',
            '#201E1C',
            ''
          ),
          [this.lg.c.headerBlockBeatPage.btnBuy]: co(
            59,
            ['.theme-btn__headerBlockBeatPage-btnBuy'],
            addBtnColor,
            addBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.headerBlockBeatPage.btnIconBuy]: co(
            60,
            ['.theme-icon__headerBlockBeatPage-btnIconBuy'],
            addBtnColor,
            '',
            ''
          ),
          [this.lg.c.headerBlockBeatPage.btnGoToCart]: co(
            73,
            ['.theme-btn__headerBlockBeatPage-btnGoToCart'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.headerBlockBeatPage.btnIconGoToCart]: co(
            80,
            ['.theme-icon__headerBlockBeatPage-btnIconGoToCart'],
            mainBtnColor,
            '',
            ''
          ),
          [this.lg.c.headerBlockBeatPage.beatPrice]: co(
            58,
            ['.theme-headerBlockBeatPage__beatPrice'],
            '#FFFFFF',
            '',
            ''
          )
        },
        app: ['PROD-PAGE']
      },
      // Beat page (license block)
      {
        title: this.lg.c.licenseBlockBeatPage.heading,
        styleItems: {
          [this.lg.c.licenseBlockBeatPage.bg]: co(28, ['.theme-licenseBlockBeatPage__bg'], '', mainBg, ''),
          [this.lg.c.licenseBlockBeatPage.title]: co(36, ['.theme-licenseBlockBeatPage__title'], mainText, '', '')
        },
        app: ['PROD-PAGE']
      },
      // Beat page (beats block)
      {
        title: this.lg.c.beatsBlockBeatPage.heading,
        styleItems: {
          [this.lg.c.beatsBlockBeatPage.bg]: co(7, ['.theme-beatsBlockBeatPage__bg'], '', mainBg, ''),
          [this.lg.c.beatsBlockBeatPage.title]: co(19, ['.theme-beatsBlockBeatPage__title'], mainText, '', '')
        },
        app: ['PROD-PAGE']
      },
      // Player
      {
        title: this.lg.c.player.heading,
        styleItems: {
          [this.lg.c.player.progressBar]: co(132, ['.theme-player__progressBar'], '', progressBar, ''),
          [this.lg.c.player.loadingBar]: co(134, ['.theme-player__loadingBar'], '', loadingBar, ''),
          [this.lg.c.player.playedBeatBar]: co(135, ['.theme-player__playedBeatBar'], '', highlight, ''),
          [this.lg.c.player.marker]: co(136, ['.theme-player__marker'], '', highlight, ''),
          [this.lg.c.player.bg]: co(34, ['.theme-player__bg'], '', mainBg, ''),
          [this.lg.c.player.beatName]: co(204, ['.theme-player__beatName'], mainText, '', ''),
          [this.lg.c.player.beatDescr]: co(205, ['.theme-player__beatDescr'], addText, '', ''),
          [this.lg.c.player.btnNextPrev]: co(130, ['.theme-player__btnNextPrev'], mainText, '', ''),
          [this.lg.c.player.btnPlay]: co(131, ['.theme-player__btnPlay'], '#FFFFFF', highlight, ''),
          [this.lg.c.player.soundHornIcon]: co(137, ['.theme-player__soundHornIcon'], mainText, '', ''),
          [this.lg.c.player.soundFilledBar]: co(208, ['.theme-player__soundFilledBar'], '', highlight, ''),
          [this.lg.c.player.soundThumb]: co(209, ['.theme-player__soundThumb'], '', highlight, ''),
          [this.lg.c.player.soundProgressBar]: co(54, ['.theme-player__soundProgressBar'], '', progressBar, ''),
          [this.lg.c.player.downloadBeatIcon]: co(138, ['.theme-player__downloadBeatIcon'], mainText, '', ''),
          [this.lg.c.player.shareBeatIcon]: co(139, ['.theme-player__shareBeatIcon'], mainText, '', ''),
          [this.lg.c.player.btnBuy]: co(140, ['.theme-btn__player-btnBuy'], addBtnColor, addBtnBg, mainBtnBorder),
          [this.lg.c.player.btnIconBuy]: co(141, ['.theme-icon__player-btnIconBuy'], addBtnColor, '', ''),
          [this.lg.c.player.btnGoToCart]: co(
            142,
            ['.theme-btn__player-btnGoToCart'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.player.btnIconGoToCart]: co(143, ['.theme-icon__player-btnIconGoToCart'], mainBtnColor, '', ''),
          [this.lg.c.player.toPlayerMobLgIcon]: co(144, ['.theme-player-toPlayerMobLgIcon'], mainText, '', '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Pop-up: select license
      {
        title: this.lg.c.licenseBlockPopUp.heading,
        styleItems: {
          [this.lg.c.licenseBlockPopUp.bg]: co(61, ['.theme-popup-licenses'], mainText, mainBg, '#383838'),
          [this.lg.c.licenseBlockPopUp.iconCancel]: co(67, ['.theme-popup-iconClose'], mainText, '', ''),
          [this.lg.c.licenseBlockPopUp.licenseItem]: co(68, ['.theme-popup-licenseItem'], mainText, mainBg, mainBorder),
          [this.lg.c.licenseBlockPopUp.licenseItemHover]: co(78, ['.theme-popup-licenseItem-hover'], '', addBg, ''),
          [this.lg.c.licenseBlockPopUp.licenseItemActive]: co(
            69,
            ['.theme-popup-licenseItemActive'],
            '',
            mainBg,
            highlight
          ),
          [this.lg.c.licenseBlockPopUp.licensePrice]: co(70, ['.theme-popup-licensePriceColor'], highlight, '', ''),
          [this.lg.c.licenseBlockPopUp.licenseCheckbox]: co(
            71,
            ['.theme-popup-licenseCheckColor'],
            '',
            addBg,
            mainBorder
          ),
          [this.lg.c.licenseBlockPopUp.licenseCheckboxActiveBorder]: co(
            72,
            ['.theme-popup-licenseCheckBorder'],
            '',
            '',
            highlight
          ),
          [this.lg.c.licenseBlockPopUp.licenseCheckboxArrowActive]: co(
            74,
            ['.theme-popup-licenseCheckColorActive'],
            highlight,
            '',
            ''
          ),
          [this.lg.c.licenseBlockPopUp.licensePopUpBtn]: co(
            75,
            ['.theme-btn__license-popUp'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.licenseBlockPopUp.licensePopUpIcon]: co(76, ['.theme-icon__license-popUp'], mainBtnColor, '', ''),
          [this.lg.c.licenseBlockPopUp.licenseFeaturesList]: co(
            77,
            ['.theme-popup-license-features-list'],
            '#FFFFFF',
            '',
            ''
          ),
          [this.lg.c.licenseBlockPopUp.licenseDescriptionDots]: co(
            79,
            ['.theme-popup-licenseDots-after'],
            '',
            highlight,
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Pop-up: checkout (cart)
      {
        title: this.lg.c.cartPopup.heading,
        styleItems: {
          [this.lg.c.cartPopup.bg]: co(82, ['.theme-cartPopup__bg'], '', mainBg, ''),
          [this.lg.c.cartPopup.title]: co(83, ['.theme-cartPopup__title'], mainText, '', ''),
          [this.lg.c.cartPopup.closeIcon]: co(84, ['.theme-cartPopup__closeIcon'], mainText, '', ''),
          [this.lg.c.cartPopup.beatName]: co(126, ['.theme-cartPopup__beatName'], mainText, '', ''),
          [this.lg.c.cartPopup.beatDescr]: co(127, ['.theme-cartPopup__beatDescr'], addText, '', ''),
          [this.lg.c.cartPopup.beatPrice]: co(93, ['.theme-cartPopup__beatPrice'], mainText, '', ''),
          [this.lg.c.cartPopup.editLicenseIcon]: co(9, ['.theme-cartPopup__editLicenseIcon'], mainText, '', ''),
          [this.lg.c.cartPopup.deleteBeatIcon]: co(200, ['.theme-cartPopup__deleteBeatIcon'], mainText, '', ''),
          [this.lg.c.cartPopup.discountPrice]: co(95, ['.theme-cartPopup__discountPrice'], addText, '', ''),
          [this.lg.c.cartPopup.paymentTitle]: co(88, ['.theme-cartPopup__paymentTitle'], mainText, '', ''),
          [this.lg.c.cartPopup.discountTitle]: co(89, ['.theme-cartPopup__discountTitle'], addText, '', ''),
          [this.lg.c.cartPopup.finalCost]: co(90, ['.theme-cartPopup__price'], mainText, '', ''),
          [this.lg.c.cartPopup.btnGoToPayment]: co(
            86,
            ['.theme-btn__cartPopup-btnGoToPayment'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.cartPopup.btnIconGoToPayment]: co(
            87,
            ['.theme-icon__cartPopup-btnIconGoToPayment'],
            mainBtnColor,
            '',
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Pop-up: confirm order
      {
        title: this.lg.c.popUpConfirmOrder.heading,
        styleItems: {
          [this.lg.c.popUpConfirmOrder.bg]: co(145, ['.theme-popUp__confirm-order'], '', mainBg, '#383838'),
          [this.lg.c.popUpConfirmOrder.popUpTitle]: co(146, ['.theme-popUp__confirm-title'], mainText, '', ''),
          [this.lg.c.popUpConfirmOrder.iconCancel]: co(147, ['.theme-popUp__confirm-icon-cancel'], mainText, '', ''),
          [this.lg.c.popUpConfirmOrder.price]: co(148, ['.theme-popUp__confirm-price'], mainText, '', ''),
          [this.lg.c.popUpConfirmOrder.requiredField]: co(
            221,
            ['.theme-popUp__confirm-requiredField'],
            addText,
            '',
            ''
          ),
          [this.lg.c.popUpConfirmOrder.description]: co(149, ['.theme-popUp__confirm-description'], mainText, '', ''),
          [this.lg.c.popUpConfirmOrder.input]: co(
            150,
            ['.theme-input__counterInput'],
            mainText,
            mainInputBg,
            mainInputBorder
          ),
          [this.lg.c.popUpConfirmOrder.inputPlaceholder]: co(
            151,
            ['.theme-input__counterInput-placeholder'],
            mainInputPlaceholder,
            '',
            ''
          ),
          [this.lg.c.popUpConfirmOrder.iconInfo]: co(152, ['.theme-input__counterIconInfo'], mainText, '', ''),
          [this.lg.c.popUpConfirmOrder.tooltip]: co(154, ['.theme-input__counterTooltip'], mainText, mainBg, ''),
          [this.lg.c.popUpConfirmOrder.button]: co(
            155,
            ['.theme-btn__counter-btnBuy'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.popUpConfirmOrder.btnIcon]: co(156, ['.theme-icon__counter-btnBuy'], mainBtnColor, '', ''),
          [this.lg.c.popUpConfirmOrder.payPalEn]: co(157, ['.theme-icon__counter__payPalEn'], mainText, '', '')
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // MU Select (confirmPopup)
      {
        title: this.lg.c.selectMU.heading.confirmPopup,
        styleItems: {
          [this.lg.c.selectMU.select.border]: co(153, ['.theme-select__selectBorder--confirmPopup'], addText, '', ''),
          [this.lg.c.selectMU.select.borderHover]: co(
            182,
            ['.theme-select__selectBorderHover--confirmPopup'],
            '#4F4D4D',
            '',
            ''
          ),
          [this.lg.c.selectMU.select.groupName]: co(
            183,
            ['.theme-select__selectGroupName--confirmPopup'],
            addText,
            '',
            ''
          ),
          [this.lg.c.selectMU.select.currentValue]: co(
            184,
            ['.theme-select__selectCurrentValue--confirmPopup'],
            mainText,
            '',
            ''
          ),
          [this.lg.c.selectMU.select.arrow]: co(185, ['.theme-select__selectArrow--confirmPopup'], mainText, '', ''),
          [this.lg.c.selectMU.options.item]: co(186, ['.theme-select__option--confirmPopup'], mainText, mainBg, ''),
          [this.lg.c.selectMU.options.selectedItem]: co(
            187,
            ['.theme-select__selectedOption--confirmPopup'],
            highlight,
            hoverBeat,
            ''
          ),
          [this.lg.c.selectMU.options.hoverItem]: co(
            188,
            ['.theme-select__hoveredOption-hover--confirmPopup'],
            highlight,
            hoverBeat,
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Pop-up: share track
      {
        title: this.lg.c.shareTrackPopup.heading,
        styleItems: {
          [this.lg.c.shareTrackPopup.bg]: co(110, ['.theme-shareTrackPopup__bg'], '', mainBg, mainBorder),
          [this.lg.c.shareTrackPopup.title]: co(111, ['.theme-shareTrackPopup__title'], mainText, '', ''),
          [this.lg.c.shareTrackPopup.closeIcon]: co(114, ['.theme-shareTrackPopup__closeIcon'], mainText, '', ''),
          [this.lg.c.shareTrackPopup.beatName]: co(124, ['.theme-shareTrackPopup__beatName'], mainText, '', ''),
          [this.lg.c.shareTrackPopup.beatDescr]: co(125, ['.theme-shareTrackPopup__beatDescr'], addText, '', ''),
          [this.lg.c.shareTrackPopup.linkTitle]: co(116, ['.theme-shareTrackPopup__linkTitle'], addText, '', ''),
          [this.lg.c.shareTrackPopup.linkInput]: co(
            119,
            ['.theme-shareTrackPopup__linkInput'],
            mainText,
            mainInputBg,
            mainInputBorder
          ),
          [this.lg.c.shareTrackPopup.btnCopyLink]: co(
            120,
            ['.theme-btn__shareTrackPopup-btnCopyLink'],
            mainBtnColor,
            mainBtnBg,
            mainBtnBorder
          ),
          [this.lg.c.shareTrackPopup.btnCopyLinkIcon]: co(
            121,
            ['.theme-icon__shareTrackPopup-btnCopyLinkIcon'],
            mainBtnColor,
            '',
            ''
          ),
          [this.lg.c.shareTrackPopup.btnLinkCopied]: co(
            122,
            ['.theme-btn__shareTrackPopup-btnLinkCopied'],
            '#FFFFFF',
            disabledBtnBg,
            disabledBtnBg
          ),
          [this.lg.c.shareTrackPopup.btnLinkCopiedIcon]: co(
            123,
            ['.theme-icon__shareTrackPopup-btnLinkCopiedIcon'],
            '#FFFFFF',
            '',
            ''
          )
        },
        app: ['WIDGET', 'PROD-PAGE']
      },
      // Pop-up: cookie
      {
        title: this.lg.c.cookieNotification.heading,
        styleItems: {
          [this.lg.c.cookieNotification.bg]: co(174, ['.theme-cookieNotification__bg'], '#FFFFFF', hfBg, ''),
          [this.lg.c.cookieNotification.link]: co(175, ['.theme-cookieNotification__link'], '#FFFFFF', '', ''),
          [this.lg.c.cookieNotification.button]: co(
            176,
            ['.theme-btn__cookieNotification'],
            '#FFFFFF',
            mainBtnBg,
            mainBtnBorder
          )
        },
        app: ['PROD-PAGE']
      },
      // User agreement page
      {
        title: this.lg.c.userAgreement.heading,
        styleItems: {
          [this.lg.c.userAgreement.bg]: co(160, ['.theme-userAgreement-bg'], mainText, mainBg, ''),
          [this.lg.c.userAgreement.mainTitle]: co(171, ['.theme-userAgreement__mainTitle'], mainText, '', ''),
          [this.lg.c.userAgreement.subTitle]: co(172, ['.theme-userAgreement__subtitle'], mainText, '', ''),
          [this.lg.c.userAgreement.link]: co(173, ['.theme-userAgreement__link'], '#56B7F4', '', '')
        },
        app: ['PROD-PAGE']
      },
      // Cookies Page
      {
        title: this.lg.c.cookiePage.heading,
        styleItems: {
          [this.lg.c.cookiePage.bg]: co(166, ['.theme-cookiePage__bg'], mainText, mainBg, ''),
          [this.lg.c.cookiePage.mainTitle]: co(167, ['.theme-cookiePage__mainTitle'], mainText, '', ''),
          [this.lg.c.cookiePage.subTitle]: co(168, ['.theme-cookiePage__subTitle'], mainText, '', ''),
          [this.lg.c.cookiePage.link]: co(170, ['.theme-cookiePage__link'], '#56B7F4', '', '')
        },
        app: ['PROD-PAGE']
      },
      // Page not Found
      {
        title: this.lg.c.notFoundPage.heading,
        styleItems: {
          [this.lg.c.notFoundPage.bg]: co(161, ['.theme-notFoundPage__bg'], '', mainBg, ''),
          [this.lg.c.notFoundPage.mainImg]: co(162, ['.theme-notFoundPage__mainImg'], mainText, '', ''),
          [this.lg.c.notFoundPage.mainTitle]: co(163, ['.theme-notFoundPage__mainTitle'], mainText, '', ''),
          [this.lg.c.notFoundPage.descr]: co(164, ['.theme-notFoundPage__descr'], mainText, '', ''),
          [this.lg.c.notFoundPage.btn]: co(165, ['.theme-btn__notFoundPage'], mainBtnColor, mainBtnBg, mainBtnBorder)
        },
        app: ['PROD-PAGE']
      },
      // Page not Found
      {
        title: this.lg.c.loader.heading,
        styleItems: {
          [this.lg.c.loader.common]: co(220, ['.theme-loader__common'], '', highlight, '')
        },
        app: ['PROD-PAGE']
      }
    ]
    return this.groups
  }

  createObj(id, selectors, color, bgColor, borderColor, title = '', settings = {}): IStyleItems {
    let defaultSettings = {
      borderAndBgConnected: false
    }

    defaultSettings = {
      ...defaultSettings,
      ...settings
    }

    return {
      id,
      selectors,
      borderAndBgConnected: defaultSettings.borderAndBgConnected,
      isChecked: false,
      colors: {
        color,
        bgColor,
        borderWidth: '1px ',
        borderStyle: 'solid',
        borderColor
      },
      title
    }
  }

  transformStylesFromServer(styles) {
    styles.forEach(colorObjectFromServer => {
      this.groups.forEach(item => {
        for (const key in item.styleItems) {
          if (item.styleItems[key].id === colorObjectFromServer.id) {
            item.styleItems[key].colors.color = colorObjectFromServer.colors.color
            item.styleItems[key].colors.bgColor = colorObjectFromServer.colors.bgColor
            item.styleItems[key].colors.borderColor = colorObjectFromServer.colors.borderColor
          }
        }
      })
    })
  }

  refreshStyleTag(theme?: ThemeTitleType) {
    this.findDuplicateId()
    this.groups = this.setGroupsTheme(theme)

    const groupItemStyles = this.groups.map(item => {
      return item.styleItems
    })

    const resultStyles = this.generateStyleTagContent(groupItemStyles)
    this.addStyleTagToPage(resultStyles)
  }

  findDuplicateId() {
    const groupStyleIds = []
    this.groups.forEach(group => {
      const obj = group.styleItems
      for (const key in obj) {
        groupStyleIds.push(obj[key].id)
      }
    })

    const sortedIdsArray = groupStyleIds.sort((a, b) => a - b)
    // console.log('sortedIdsArray: ', sortedIdsArray);

    sortedIdsArray.forEach((groupStyleId, index, array) => {
      if (array[index + 1] === array[index]) {
        alert(`You have duplicate id: ${groupStyleId}. Change ID in set-all-color-service.`)
      }
    })
  }

  generateStyleTagContent(styles): string {
    let resultStyles = ''

    styles.forEach(item => {
      for (const key in item) {
        const selector = item[key].selectors.reduce((accum, el, index) => {
          // customize hover and placeholder effects
          if (!index && el.indexOf('hover') > 0) {
            return `${el}:hover`
          }
          if (!index && el.indexOf('placeholder') > 0) {
            return `${el}::placeholder`
          }
          if (!index && el.indexOf('after') > 0) {
            return `${el}:after`
          }
          if (el.indexOf('hover') > 0) {
            return `${accum}, ${el}:hover`
          }
          if (el.indexOf('placeholder') > 0) {
            return `${accum}, ${el}::placeholder`
          }
          if (el.indexOf('after') > 0) {
            return `${accum}, ${el}:after`
          }
          if (!index) {
            return `${el}`
          }
          return `${accum}, ${el}`
        }, '')
        const style = item[key].colors
        const widthAndStyle = style.borderWidth + style.borderStyle
        const styleString = `{
                            color: ${style.color} !important;
                            background-color: ${style.bgColor} !important;
                            border: ${!!style.borderColor || item[key].borderAndBgConnected ? widthAndStyle : ''}
                                    ${item[key].borderAndBgConnected ? style.bgColor : style.borderColor} !important;
                }`

        resultStyles += `${selector}${styleString}`
      }
    })

    return resultStyles
  }

  public addStyleTagToPage(styles: string) {
    this.deleteStyleTagFromPage()
    this.styleElement = document.createElement('style')
    this.styleElement.id = this.styleElementId
    this.styleElement.innerHTML = styles
    const headElement = document.querySelector('head')
    headElement.appendChild(this.styleElement)
  }

  private deleteStyleTagFromPage() {
    const styleElement = document.querySelector(`#${this.styleElementId}`)
    if (!!styleElement) {
      styleElement.remove()
    }
  }

  getGroups() {
    return this.groups
  }

  notifyComponent(boolToBLockPosition: boolean) {
    this.colorSettingsSubject.next(boolToBLockPosition)
  }

  sendSettings(appType: AppType, theme: ThemeTitleType) {
    const body = {
      theme,
      styles: this.getTransformStyles()
    }

    if (appType === 'PROD-PAGE') {
      return this.http.post<ResponseModel>(this.environmentService.saveColorsUrl(), body, { withCredentials: true })
    }

    if (appType === 'WIDGET') {
      this.widgetsApiService.widgetById$.subscribe(widget => {
        this.widget = widget
      })

      return this.widgetsApiService.updateWidget({ widget: this.widget, colors: JSON.stringify(body) })

      //
      // const responseSubject = new Subject<ResponseModel>()
      //
      // updateColorsEvent?.emit({ colors: JSON.stringify(body), responseSubject }) // ✅ Теперь типизировано
      //
      // return responseSubject.asObservable()
      //
    }
  }

  /* For producer page only. Work in staging */
  resetSettings() {
    return this.http.get(this.environmentService.resetColors()).subscribe(res => console.log(res))
  }

  setTheme(theme: ThemeTitleType) {
    this.currentThemeTitle = theme
  }

  getTransformStyles() {
    const transformStyles = this.groups.reduce((acc, item) => {
      for (const key in item.styleItems) {
        acc.push({
          id: item.styleItems[key].id,
          colors: {
            color: item.styleItems[key].colors.color,
            bgColor: item.styleItems[key].colors.bgColor,
            borderColor: item.styleItems[key].colors.borderColor
          }
        })
      }
      return acc
    }, [])

    return transformStyles
  }
}
